import * as z from 'zod/mini';
import { MFA_CODE_MAX_LENGTH, PASSWORD_MAX_LENGTH } from '../constants.ts';

// The second factor's wire shapes. A code is what the authenticator app
// shows (six digits) or a recovery code (eight base32 characters, shown
// as xxxx-xxxx); the Worker tells them apart by shape.

const codeSchema = z.string().check(z.minLength(1), z.maxLength(MFA_CODE_MAX_LENGTH));

/** The second step of a sign-in: the code from the app, or a recovery code. */
export const mfaChallengeInputSchema = z.strictObject({ code: codeSchema });

/** What POST /api/auth/login answers when a second factor is needed. */
export const loginResultSchema = z.object({ mfaRequired: z.boolean() });
export type LoginResult = z.infer<typeof loginResultSchema>;

/** Enrolment step one: the secret to enter or scan. */
export const mfaSetupSchema = z.object({
  /** Base32, for typing into the app by hand. */
  secret: z.string(),
  /** The otpauth:// URI the app scans as a QR code. */
  uri: z.string(),
});
export type MfaSetup = z.infer<typeof mfaSetupSchema>;

/** Enrolment step two, and a recovery-code refresh: prove the app works. */
export const mfaCodeInputSchema = z.strictObject({ code: codeSchema });

/** Turning the second factor off takes the password and a code. */
export const mfaDisableInputSchema = z.strictObject({
  password: z.string().check(z.minLength(1), z.maxLength(PASSWORD_MAX_LENGTH)),
  code: codeSchema,
});

/** The one time the recovery codes are shown. */
export const recoveryCodesSchema = z.object({ recoveryCodes: z.array(z.string()) });
export type RecoveryCodes = z.infer<typeof recoveryCodesSchema>;

/** Where the account stands: enabled, and how many recovery codes are left. */
export const mfaStatusSchema = z.object({
  enabled: z.boolean(),
  recoveryCodesLeft: z.number(),
});
export type MfaStatus = z.infer<typeof mfaStatusSchema>;
